import { Component } from "react";
import { type RouteComponentProps } from "react-router";

import { setLoadingAction } from "utilities/form/form";

import { BTModal, type IModalConfiguration } from "commonComponents/btWrappers/BTModal/BTModal";
import { withErrorBoundary } from "commonComponents/helpers/ErrorBoundary/ErrorBoundary";
import { BTLoading } from "commonComponents/utilities/BTLoading/BTLoading";

import {
    type IInternalUserDeleteConflictsFormValues,
    type InternalUserDeleteConflictsEntity,
    type InternalUserDeleteConflictsFormActions,
} from "./InternalUserDeleteConflicts.api.types";
import { InternalUserDeleteConflictsPresentational } from "./InternalUserDeleteConflictsPresentational";

interface IUrlProps {}

interface IInternalUserDeleteConflictsProps extends RouteComponentProps<IUrlProps> {
    loginId: number;
    internalUserName: string;
    entity: InternalUserDeleteConflictsEntity;
    onReassignJobsitesForUserDeletion: (newLoginId: number) => Promise<void>;
    onReassignItemsForUserDeletion: (
        formValues: IInternalUserDeleteConflictsFormValues,
    ) => Promise<void>;
    modalConfig?: IModalConfiguration;
}

interface IInternalUserDeleteConflictsState {
    actionBeingPerformed: InternalUserDeleteConflictsFormActions;
}

class InternalUserDeleteConflictsInternal extends Component<
    IInternalUserDeleteConflictsProps,
    IInternalUserDeleteConflictsState
> {
    state: Readonly<IInternalUserDeleteConflictsState> = {
        actionBeingPerformed: undefined,
    };

    private handleReassignJobsitesForUserDeletion = async (newLoginId: number) => {
        await setLoadingAction<InternalUserDeleteConflictsFormActions>(this, {
            actionBeingPerformed: "submitting",
            callback: async () => await this.props.onReassignJobsitesForUserDeletion(newLoginId),
        });
    };

    private handleReassignItemsForUserDeletion = async (
        formValues: IInternalUserDeleteConflictsFormValues,
    ) => {
        await setLoadingAction<InternalUserDeleteConflictsFormActions>(this, {
            actionBeingPerformed: "submitting",
            callback: async () => await this.props.onReassignItemsForUserDeletion(formValues),
        });
    };

    render() {
        // Show loading spinner until entity is loaded
        if (!this.state) {
            return <BTLoading />;
        }

        const { modalConfig } = this.props;

        const component = (
            <InternalUserDeleteConflictsPresentational
                loginId={this.props.loginId}
                internalUserName={this.props.internalUserName}
                entity={this.props.entity}
                onReassignJobsitesForUserDeletion={this.handleReassignJobsitesForUserDeletion}
                onReassignItemsForUserDeletion={this.handleReassignItemsForUserDeletion}
                actionBeingPerformed={this.state.actionBeingPerformed}
                modalConfig={modalConfig}
            />
        );

        if (modalConfig) {
            return (
                <BTModal
                    data-testid="btModalInternalUserDeleteConflicts"
                    title="Internal User Delete Conflicts"
                    beforeClose={modalConfig.beforeClose}
                    width="800px"
                    visible
                    removeBodyPadding
                    useModalLayout
                >
                    {component}
                </BTModal>
            );
        }
        return component;
    }
}

export const InternalUserDeleteConflicts = withErrorBoundary(InternalUserDeleteConflictsInternal)(
    "Could not load Internal User Delete Conflicts",
);
export default InternalUserDeleteConflicts;
